/**
 * Hook for managing MCP servers and skills in the assistant.
 * Extracted from useAssistant to provide the same features
 * without conversation/streaming management.
 */
import { useCallback, useEffect, useState } from "react";
import type { AssistantApi } from "~/components/assistant/api/AssistantApi";
import { useMcpGuidelines } from "~/components/assistant/hooks/useMcpGuidelines";
import { useSkillsFeature } from "~/components/assistant/hooks/useSkillsFeature";
import type { MCPServer } from "~/components/assistant/types";
import useUser from "~/contexts/user";
import { useConversationSkills } from "~/pages/datasets/components/assistant/hooks/useConversationSkills";
import {
  createUserSkill as postUserSkill,
  deleteUserSkill,
  detachAgentSkill,
  detachDatasetSkill,
  getSkill as fetchSkill,
  invalidateAllSkillLists,
  type PartialSkill,
  type Skill,
  type SkillFields,
  updateSkill as patchSkill,
} from "~/services/skills";
import { trackEvent } from "~/utils/monitoring";

interface UseAssistantV2FeaturesOptions {
  api: AssistantApi;
  contextId: string;
  /**
   * Agent UUID of the current conversation. Drives the AGENT-scope leg of
   * the picker union. Null/undefined disables AGENT fetching.
   */
  agentId?: string | null;
  /**
   * Dataset UUID of the current conversation. Drives the DATASET-scope leg
   * of the picker union. Null/undefined disables DATASET fetching.
   */
  datasetId?: string | null;
  enableSkills?: boolean;
}

export function useAssistantFeatures({
  api,
  contextId,
  agentId,
  datasetId,
  enableSkills = false,
}: UseAssistantV2FeaturesOptions) {
  const skillsFeatureFlag = useSkillsFeature();
  const skillsEnabled = enableSkills && skillsFeatureFlag;

  const {
    hasAccepted: mcpGuidelinesAccepted,
    isAccepting: isAcceptingGuidelines,
    acceptGuidelines,
  } = useMcpGuidelines(api);

  const [mcpServers, setMcpServers] = useState<MCPServer[]>([]);
  const [selectedMcpServers, setSelectedMcpServers] = useState<string[]>([]);

  const user = useUser();
  const skillsAgentId = skillsEnabled && agentId ? agentId : null;
  const skillsDatasetId = skillsEnabled && datasetId ? datasetId : null;
  const skillsUserId = skillsEnabled ? (user?.email ?? null) : null;
  const { skills, isLoading: isLoadingSkills } = useConversationSkills(
    skillsAgentId,
    skillsDatasetId,
    skillsUserId
  );

  const toggleMcpServer = useCallback(
    (serverId: string) => {
      setSelectedMcpServers((prev) => {
        const isCurrentlySelected = prev.includes(serverId);
        const newSelection = isCurrentlySelected
          ? prev.filter((id) => id !== serverId)
          : [...prev, serverId];

        const server = mcpServers.find((s) => s.id === serverId);
        trackEvent("assistant_v2_mcp_server_toggled", {
          dataset_id: contextId,
          context: "dataset",
          server_id: serverId,
          server_name: server?.name || serverId,
          enabled: !isCurrentlySelected,
          total_selected: newSelection.length,
        });

        return newSelection;
      });
    },
    [mcpServers, contextId]
  );

  const createUserSkill = useCallback(async (fields: SkillFields) => {
    await postUserSkill(fields);
    await invalidateAllSkillLists();
  }, []);

  const updateSkill = useCallback(async (skillId: string, fields: Partial<SkillFields>) => {
    await patchSkill(skillId, fields);
    await invalidateAllSkillLists();
  }, []);

  const deleteSkill = useCallback(async (skill: PartialSkill) => {
    switch (skill.scope) {
      case "USER":
        await deleteUserSkill(skill.id);
        break;
      case "AGENT":
        if (!skill.scope_id) {
          throw new Error(`AGENT-scope skill ${skill.id} is missing scope_id`);
        }
        await detachAgentSkill(skill.scope_id, skill.id);
        break;
      case "DATASET":
        if (!skill.scope_id) {
          throw new Error(`DATASET-scope skill ${skill.id} is missing scope_id`);
        }
        await detachDatasetSkill(skill.scope_id, skill.id);
        break;
      default:
        throw new Error(`Cannot delete ${skill.scope}-scope skill from this surface`);
    }
    await invalidateAllSkillLists();
  }, []);

  const getSkill = useCallback(async (skillId: string): Promise<Skill> => fetchSkill(skillId), []);

  // Load MCP servers on mount
  useEffect(() => {
    let cancelled = false;

    api
      .listMcpServers()
      .then((servers) => {
        if (cancelled) return;
        setMcpServers(servers);
        const defaultServerIds = servers.filter((s) => s.default).map((s) => s.id);
        setSelectedMcpServers(defaultServerIds);
      })
      .catch((error) => {
        console.error("Failed to load MCP servers:", error);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    skillsEnabled,
    mcpServers,
    selectedMcpServers,
    toggleMcpServer,
    skills,
    isLoadingSkills,
    createUserSkill,
    updateSkill,
    deleteSkill,
    getSkill,
    mcpGuidelinesAccepted,
    isAcceptingGuidelines,
    acceptGuidelines,
  };
}
